import {DatePipe} from "@angular/common"
import {Component, computed, inject, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MatInputModule} from "@angular/material/input"
import {MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {RouterLink, RouterOutlet} from "@angular/router"
import {ContentTypeModel, JobState, MutationUpdateJobInput, DataObjectAssignmentType, TemplateType} from "@generated"
import {JobDetailsFragment} from "@app/platform/components/jobs/job-details/job-details.generated"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {SectionComponent} from "@common/components/item/item-details/section/section.component"
import {IsLoadingDirective} from "@common/directives"
import {createLinkToEditorFromTemplatesForType} from "@common/helpers/routes"
import {jobIsActive} from "@common/helpers/jobs/states"
import {makeHeartbeat} from "@common/helpers/utils/heartbeat"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {FilesSectionComponent} from "@platform/components/details/files-section/files-section.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"
import {ExecutionPlanComponent} from "@platform/components/jobs/execution-plan/execution-plan.component"
import {
    JobDetailsCancelGQL,
    JobDetailsDiscardGQL,
    JobDetailsGQL,
    JobDetailsNudgeGQL,
    JobDetailsRerunAllTasksInJobGQL,
    JobDetailsRestartGQL,
    UpdateJobDetailsGQL,
} from "@platform/components/jobs/job-details/job-details.generated"
import {JobTasksTableComponent} from "@platform/components/jobs/job-tasks-table/job-tasks-table.component"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"
import {CopyIdButtonComponent} from "@common/components/buttons/copy-id-button/copy-id-button.component"
import {JobRunTimelineComponent} from "@platform/components/jobs/job-run-timeline/job-run-timeline.component"
import {CopyValueToClipboardMenuComponent} from "@platform/components/shared/copy-value-to-clipboard-menu/copy-value-to-clipboard-menu.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {catchError, EMPTY, from, map, Observable, tap} from "rxjs"
import {TabStateService} from "@common/services/tab-state/tab-state.service"
import {MatTooltip} from "@angular/material/tooltip"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-details",
    templateUrl: "job-details.component.html",
    styleUrls: ["job-details.component.scss"],
    imports: [
        DetailsDialogLayoutComponent,
        SectionComponent,
        ExecutionPlanComponent,
        JobTasksTableComponent,
        JobRunTimelineComponent,
        MatInputModule,
        MatSelectModule,
        IsLoadingDirective,
        FormsModule,
        MatCheckboxModule,
        MatButtonModule,
        RoutedDialogComponent,
        RoutedDialogComponent,
        MatMenuModule,
        FilesSectionComponent,
        TitleSectionComponent,
        CopyValueToClipboardMenuComponent,
        OrganizationSelectComponent,
        CopyIdButtonComponent,
        RouterOutlet,
        RouterLink,
        DatePipe,
        MatTooltip,
        TimeAgoPipe,
    ],
})
export class JobDetailsComponent extends BaseDetailsComponent<JobDetailsFragment, Omit<MutationUpdateJobInput, "id">> {
    override _contentTypeModel = ContentTypeModel.JobTask
    override _fetchItem = fetchThrowingErrors(inject(JobDetailsGQL))
    override _updateItem = mutateThrowingErrors(inject(UpdateJobDetailsGQL))

    readonly tabState = inject(TabStateService)

    readonly cancelGql = inject(JobDetailsCancelGQL)
    readonly discardGql = inject(JobDetailsDiscardGQL)
    readonly nudgeGql = inject(JobDetailsNudgeGQL)
    readonly restartGql = inject(JobDetailsRestartGQL)
    readonly rerunAllGql = inject(JobDetailsRerunAllTasksInJobGQL)
    readonly updateJobGql = inject(UpdateJobDetailsGQL)

    readonly $overlayDialogComponent = viewChild.required<RoutedDialogComponent>("overlayDialogComponent")
    // fires whenever the details view and its subcomponents should refresh their data
    // this might be triggered by an explicit refresh, a timer, the user returning to the app, etc.

    // fires whenever the details view and its subcomponents should refresh their data
    // this might be triggered by an explicit refresh, a timer, the user returning to the app, etc.
    protected heartbeat$: Observable<string | null> = EMPTY

    // Detail routes for the entity types a job can be assigned to. Types without a route
    // (e.g. Set, Project) fall through to a non-clickable label.
    private readonly entityRoutes: Partial<Record<string, string>> = {
        Material: "/materials",
        Template: "/templates",
        Picture: "/pictures",
        Model: "/models",
    }

    protected entityRouterLink(assignedTo: {__typename?: string; id?: string | null} | null | undefined): string[] | null {
        const base = assignedTo?.__typename ? this.entityRoutes[assignedTo.__typename] : undefined
        return base && assignedTo?.id ? [base, assignedTo.id] : null
    }

    // Direct link into the template editor for a Template subject. Requires the template's latest revision,
    // which the route is keyed on; returns null for non-templates or templates without a revision.
    protected templateEditorLink(
        assignedTo: {__typename?: string; id?: string | null; type?: TemplateType; latestRevision?: {id: string} | null} | null | undefined,
    ): string[] | null {
        if (assignedTo?.__typename !== "Template" || !assignedTo.id || !assignedTo.latestRevision?.id) {
            return null
        }
        return createLinkToEditorFromTemplatesForType(this.templateEditorBase(assignedTo.type), assignedTo.id, assignedTo.latestRevision.id)
    }

    // Scenes and products live under their own editor base paths; everything else opens under /templates.
    private templateEditorBase(type: TemplateType | undefined): "templates" | "scenes" | "products" {
        switch (type) {
            case TemplateType.Scene:
                return "scenes"
            case TemplateType.Product:
                return "products"
            default:
                return "templates"
        }
    }

    // The texture editor is reached under a material: /materials/:materialId/set-revisions/:revisionId.
    // A TextureSet subject reaches it via its texture group's material and its latest (newest-first) revision.
    protected textureSetEditorLink(
        assignedTo:
            | {__typename?: string; textureGroup?: {material?: {id: string} | null} | null; textureSetRevisions?: ReadonlyArray<{id: string}> | null}
            | null
            | undefined,
    ): string[] | null {
        if (assignedTo?.__typename !== "TextureSet") {
            return null
        }
        const materialId = assignedTo.textureGroup?.material?.id
        const revisionId = assignedTo.textureSetRevisions?.[0]?.id
        return materialId && revisionId ? ["/materials", materialId, "set-revisions", revisionId] : null
    }

    // At least one subject: the material output or any job assignment. Assignments always render
    // (label + object id); only those whose type has a detail route are clickable.
    protected readonly $hasAssociatedSubjects = computed(() => {
        const item = this.$item()
        return !!item?.materialOutput?.material || (item?.assignments?.length ?? 0) > 0
    })

    override ngOnInit() {
        this.heartbeat$ = makeHeartbeat(
            this.itemId$,
            this.refresh,
            this.tabState,
            (variables) => from(this._fetchItem(variables).then(({item}) => item)),
            jobIsActive,
        ).pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((item) => {
                this.item$.next(item)
                this.$loadError.set(null)
            }),
            catchError((error) => {
                this.item$.next(null)
                this.$loadError.set(error)
                return EMPTY
            }),
            map((item) => item.id),
        )
        this.debounceUpdates()
    }

    readonly $copyItems = computed(() => {
        const result: {value: string | undefined | (() => string | undefined); displayText: string; icon: string}[] = [
            {value: this.$item()?.legacyId?.toString(), displayText: "Legacy ID", icon: "far fa-person-cane"},
            {value: this.$item()?.id, displayText: "ID", icon: "far fa-id-badge"},
        ]
        if (this.$can().read.job(null, "jsonGraph")) {
            result.push({
                value: this.getGraph,
                displayText: "JSON Graph",
                icon: "data_object",
            })
        }
        return result
    })

    cancelJob() {
        void this.notifications.withUserFeedback(
            async () => {
                const item = this.$item()
                if (item) {
                    await mutateThrowingErrors(this.cancelGql)({id: item.id})
                    this.refresh.item(item)
                } else {
                    throw new Error("No job to cancel")
                }
            },
            {
                success: "Job cancelled",
                error: "Failed to cancel job",
            },
        )
    }

    // Retire the job from its assignment slot for good, e.g. to free the slot for another job to restart.
    async discardJob() {
        const confirmed = await this.notifications.confirmationDialog({
            title: "Discard job?",
            message:
                "The job is kept for reference but retired from its slot, and stopped if it is still running. A discarded job <strong>cannot be restarted</strong>.",
            confirm: "Discard",
            isDestructive: true,
        })
        if (!confirmed) return
        void this.notifications.withUserFeedback(
            async () => {
                const item = this.$item()
                if (!item) throw new Error("No job to discard")
                await mutateThrowingErrors(this.discardGql)({id: item.id})
                this.refresh.item(item)
            },
            {
                success: "Job discarded",
                error: "Failed to discard job",
            },
        )
    }

    nudgeJob() {
        void this.notifications.withUserFeedback(
            async () => {
                const item = this.$item()
                if (item) {
                    await mutateThrowingErrors(this.nudgeGql)({id: item.id})
                    this.refresh.item(item)
                } else {
                    throw new Error("No job to cancel")
                }
            },
            {
                success: "Job nudged",
                error: "Failed to nudge job",
            },
        )
    }

    restartJob() {
        void this.notifications.withUserFeedback(
            async () => {
                const item = this.$item()
                if (item) {
                    await mutateThrowingErrors(this.restartGql)({id: item.id})
                    this.refresh.item(item)
                } else {
                    throw new Error("No job to restart")
                }
            },
            {
                success: "Job restarted",
                error: "Failed to restart job",
            },
        )
    }

    rerunAllTasksInJob() {
        void this.notifications.withUserFeedback(
            async () => {
                const item = this.$item()
                if (item) {
                    await mutateThrowingErrors(this.rerunAllGql)({id: item.id})
                    this.refresh.item(item)
                } else {
                    throw new Error("No job found")
                }
            },
            {
                success: "Job cleaned and restarted",
                error: "Failed to clean job",
            },
        )
    }

    taskClick(task: {id: string}) {
        void this.router.navigate([task.id], {relativeTo: this.route, queryParamsHandling: "preserve"})
    }

    togglePriority(highPriority: boolean) {
        void this.notifications.withUserFeedback(
            async () => {
                const item = this.$item()
                if (item) {
                    await mutateThrowingErrors(this.updateJobGql)({input: {id: item.id, priority: highPriority ? 50 : 0}})
                    this.refresh.item(item)
                } else {
                    throw new Error("No job to change priority of")
                }
            },
            {
                success: "Job priority changed",
                error: "Failed to change job priority",
            },
        )
    }

    getGraph = () => {
        const item = this.$item()
        return item ? JSON.stringify(item.graph ?? {}, null, 2) : ""
    }

    override readonly $title = computed(() => {
        const item = this.$item()
        if (!item) {
            return null
        }
        return item?.name ?? `Job ${item?.legacyId}`
    })

    async closeDialog() {
        await this.router.navigate(["jobs"], {queryParamsHandling: "preserve"})
    }

    protected readonly DialogSize = DialogSize
    protected readonly JobState = JobState
    protected readonly ContentTypeModel = ContentTypeModel
    protected readonly DataObjectAssignmentType = DataObjectAssignmentType
}
